import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, CircularProgress,
  IconButton, InputAdornment, 
} from "@mui/material";
import { FaLock, FaEye, FaEyeSlash, FaSun, FaMoon, FaCheckCircle, FaShieldAlt, FaKey } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../services/api";

const pwScore = (pw) => {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 6)           s++;
  if (pw.length >= 10)          s++;
  if (/[A-Z]/.test(pw))        s++;
  if (/[0-9]/.test(pw))        s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
};
const strengthLabel = ["", "Weak", "Fair", "Good", "Strong", "Very Strong"];
const strengthColor = ["", "#ef4444", "#f59e0b", "#3b82f6", "#10b981", "#6366f1"];

const REQUIREMENTS = [
  { label: "At least 6 characters",        test: (p) => p.length >= 6 },
  { label: "At least one uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "At least one number",           test: (p) => /[0-9]/.test(p) },
  { label: "At least one special character",test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const ResetPasswordForm = () => {
  const navigate            = useNavigate();
  const [searchParams]      = useSearchParams();
  const token               = searchParams.get("token") || "";

  const [pw, setPw]           = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw]   = useState(false);
  const [showCf, setShowCf]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const [error, setError]     = useState("");
  const [isDark, setIsDark]   = useState(() => localStorage.getItem("theme") === "dark");

  useEffect(() => {
    const html = document.querySelector("html");
    if (!html) return;
    const obs = new MutationObserver(() => setIsDark(html.classList.contains("dark-theme")));
    obs.observe(html, { attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  const toggleTheme = () => {
    const html = document.querySelector("html");
    const next = html.classList.contains("dark-theme") ? "light" : "dark";
    if (next === "dark") { html.classList.add("dark-theme"); localStorage.setItem("theme", "dark"); }
    else { html.classList.remove("dark-theme"); localStorage.setItem("theme", "light"); }
    setIsDark(next === "dark");
  };

  const score = pwScore(pw);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { setError("Invalid reset link. Please request a new one."); return; }
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/api/admin/reset-password", { token, newPassword: pw });
      setDone(true);
      setTimeout(() => navigate("/sign-in/"), 4000);
    } catch (err) {
      setError(err.response?.data?.message || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  const bg = isDark
    ? "linear-gradient(135deg,#0f172a 0%,#1e1b4b 60%,#0f172a 100%)"
    : "linear-gradient(135deg,#eef2ff 0%,#f5f3ff 50%,#ecfdf5 100%)";

  if (!token) return (
    <Box sx={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background: bg, p:2 }}>
      <Box sx={{ textAlign:"center", p:4, bgcolor: isDark ? "#1e293b" : "#fff", borderRadius:"20px", boxShadow:"0 20px 60px rgba(99,102,241,0.12)" }}>
        <Box sx={{ width:64, height:64, borderRadius:"50%", bgcolor:"#fee2e2", display:"flex", alignItems:"center", justifyContent:"center", mx:"auto", mb:2 }}>
          <FaLock size={26} color="#dc2626"/>
        </Box>
        <Typography sx={{ fontSize:18, fontWeight:700, color:"#dc2626", mb:1 }}>Invalid Reset Link</Typography>
        <Typography sx={{ fontSize:13, color:"#64748b", mb:2.5 }}>This link is missing a token. Please request a new reset link.</Typography>
        <Button variant="contained" disableElevation onClick={() => navigate("/forgot-password")}
          sx={{ textTransform:"none", borderRadius:"10px", bgcolor:"#6366f1", "&:hover":{ bgcolor:"#4f46e5" } }}>
          Request New Link
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background: bg, p:2, position:"relative", overflow:"hidden" }}>

      {/* Background orbs */}
      <Box sx={{ position:"fixed", top:-120, left:-120, width:400, height:400, borderRadius:"50%", background:"radial-gradient(circle,rgba(99,102,241,0.18) 0%,transparent 70%)", pointerEvents:"none" }} />
      <Box sx={{ position:"fixed", bottom:-150, right:-100, width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle,rgba(139,92,246,0.13) 0%,transparent 70%)", pointerEvents:"none" }} />

      {/* Theme toggle */}
      <IconButton onClick={toggleTheme} sx={{
        position:"fixed", top:20, right:20, zIndex:10,
        bgcolor: isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.85)",
        backdropFilter:"blur(10px)",
        color: isDark ? "#fbbf24" : "#6366f1",
        border:`1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(99,102,241,0.18)"}`,
        "&:hover":{ bgcolor: isDark ? "rgba(255,255,255,0.13)" : "#fff" },
        width:40, height:40,
      }}>
        {isDark ? <FaSun size={15}/> : <FaMoon size={15}/>}
      </IconButton>

      {/* Main card */}
      <Box sx={{
        width:"100%", maxWidth:900,
        display:"flex", borderRadius:"24px", overflow:"hidden",
        boxShadow: isDark ? "0 32px 100px rgba(0,0,0,0.6)" : "0 32px 100px rgba(99,102,241,0.15)",
        border:`1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(99,102,241,0.1)"}`,
        minHeight:580,
      }}>

        {/* LEFT — branding */}
        <Box sx={{
          display:{ xs:"none", md:"flex" }, flexDirection:"column",
          justifyContent:"center", alignItems:"center",
          flex:"0 0 42%",
          background: isDark
            ? "linear-gradient(145deg,#1e1b4b,#312e81)"
            : "linear-gradient(145deg,#6366f1,#8b5cf6)",
          p:5, position:"relative", overflow:"hidden",
        }}>
          <Box sx={{ position:"absolute", top:-60, right:-60, width:200, height:200, borderRadius:"50%", bgcolor:"rgba(255,255,255,0.06)" }}/>
          <Box sx={{ position:"absolute", bottom:-80, left:-40, width:240, height:240, borderRadius:"50%", bgcolor:"rgba(255,255,255,0.05)" }}/>

          <Box sx={{ width:88, height:88, borderRadius:"24px", bgcolor:"rgba(255,255,255,0.15)", backdropFilter:"blur(10px)", display:"flex", alignItems:"center", justifyContent:"center", mb:3.5, border:"1px solid rgba(255,255,255,0.2)" }}>
            <FaKey size={38} color="#fff"/>
          </Box>

          <Typography sx={{ fontSize:26, fontWeight:800, color:"#fff", mb:1.5, textAlign:"center", lineHeight:1.2 }}>
            Set New Password
          </Typography>
          <Typography sx={{ fontSize:14, color:"rgba(255,255,255,0.75)", textAlign:"center", lineHeight:1.8, mb:4 }}>
            Choose a strong password to keep your admin account secure.
          </Typography>

          {[
            { icon:<FaShieldAlt size={12}/>, text:"Use 10+ characters for best security" },
            { icon:<FaCheckCircle size={12}/>, text:"Mix uppercase, numbers & symbols" },
            { icon:<FaLock size={12}/>, text:"All sessions revoked after reset" },
          ].map(({ icon, text }) => (
            <Box key={text} sx={{ display:"flex", alignItems:"center", gap:1.5, mb:1.75, alignSelf:"flex-start" }}>
              <Box sx={{ width:28, height:28, borderRadius:"8px", bgcolor:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, color:"#fff" }}>
                {icon}
              </Box>
              <Typography sx={{ fontSize:13.5, color:"rgba(255,255,255,0.85)", fontWeight:500 }}>{text}</Typography>
            </Box>
          ))}
        </Box>

        {/* RIGHT — form */}
        <Box sx={{
          flex:1, display:"flex", flexDirection:"column", justifyContent:"center",
          bgcolor: isDark ? "#0f172a" : "#fff",
          p:{ xs:3.5, sm:5 },
        }}>
          {done ? (
            <Box sx={{ textAlign:"center" }}>
              <Box sx={{
                width:88, height:88, borderRadius:"50%",
                background:"linear-gradient(135deg,#10b981,#059669)",
                display:"flex", alignItems:"center", justifyContent:"center",
                mx:"auto", mb:3,
                boxShadow:"0 16px 48px rgba(16,185,129,0.4)",
                animation:"popIn 0.45s cubic-bezier(0.175,0.885,0.32,1.275)",
                "@keyframes popIn":{ "0%":{ transform:"scale(0)", opacity:0 }, "100%":{ transform:"scale(1)", opacity:1 } },
              }}>
                <FaCheckCircle size={40} color="#fff"/>
              </Box>
              <Typography sx={{ fontSize:26, fontWeight:800, color: isDark ? "#f1f5f9" : "#0f172a", mb:1 }}>
                Password Reset!
              </Typography>
              <Typography sx={{ fontSize:14, color: isDark ? "#94a3b8" : "#64748b", lineHeight:1.8, mb:3.5 }}>
                Your password has been updated successfully. You'll be redirected to sign in shortly.
              </Typography>
              <Box sx={{ p:2, borderRadius:"12px", bgcolor: isDark ? "rgba(16,185,129,0.08)" : "#f0fdf4", border:`1px solid ${isDark ? "rgba(16,185,129,0.2)" : "#bbf7d0"}` }}>
                <Typography sx={{ fontSize:13, color: isDark ? "#6ee7b7" : "#166534" }}>
                  Redirecting to Sign In in a few seconds…
                </Typography>
              </Box>
              <Button variant="contained" disableElevation onClick={() => navigate("/sign-in/")}
                sx={{ mt:3, textTransform:"none", borderRadius:"12px", fontWeight:700, px:4, height:46,
                  background:"linear-gradient(135deg,#6366f1,#8b5cf6)", "&:hover":{ background:"linear-gradient(135deg,#4f46e5,#7c3aed)" } }}>
                Go to Sign In Now
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ width:56, height:56, borderRadius:"16px", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center", mb:3, boxShadow:"0 8px 24px rgba(99,102,241,0.3)" }}>
                <FaLock size={22} color="#fff"/>
              </Box>

              <Typography sx={{ fontSize:26, fontWeight:800, color: isDark ? "#f1f5f9" : "#0f172a", mb:0.75 }}>
                Set New Password
              </Typography>
              <Typography sx={{ fontSize:14, color: isDark ? "#94a3b8" : "#64748b", mb:3.5, lineHeight:1.75 }}>
                Choose a strong password for your admin account.
              </Typography>

              <form onSubmit={handleSubmit}>
                {/* New password */}
                <Typography sx={{ fontSize:12, fontWeight:700, color: isDark ? "#cbd5e1" : "#374151", mb:0.75, letterSpacing:"0.4px", textTransform:"uppercase" }}>
                  New Password
                </Typography>
                <TextField fullWidth size="small" label="" placeholder="Enter new password"
                  type={showPw ? "text" : "password"}
                  value={pw} onChange={(e) => { setPw(e.target.value); setError(""); }}
                  sx={{
                    mb: pw ? 1 : 2.5,
                    "& .MuiOutlinedInput-root":{ borderRadius:"12px", fontSize:14,
                      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                      "& fieldset":{ borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" },
                      "&:hover fieldset":{ borderColor:"#6366f1" },
                      "&.Mui-focused fieldset":{ borderColor:"#6366f1", borderWidth:2 },
                    },
                    "& input":{ color: isDark ? "#f1f5f9" : "#0f172a" },
                  }}
                  InputProps={{
                    startAdornment:<InputAdornment position="start"><FaLock size={13} color={isDark ? "#475569" : "#94a3b8"}/></InputAdornment>,
                    endAdornment:(
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPw((p) => !p)}>
                          {showPw ? <FaEyeSlash size={13} color="#94a3b8"/> : <FaEye size={13} color="#94a3b8"/>}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Strength bar */}
                {pw && (
                  <Box sx={{ mb:2.5 }}>
                    <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:0.75 }}>
                      <Typography sx={{ fontSize:11, color:"#94a3b8" }}>Password strength</Typography>
                      <Typography sx={{ fontSize:11, fontWeight:700, color: strengthColor[score] }}>{strengthLabel[score]}</Typography>
                    </Box>
                    <Box sx={{ display:"flex", gap:0.5 }}>
                      {[1,2,3,4,5].map((i) => (
                        <Box key={i} sx={{ flex:1, height:4, borderRadius:2, transition:"background 0.3s", bgcolor: i <= score ? strengthColor[score] : (isDark ? "#1e293b" : "#e2e8f0") }}/>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Requirements */}
                {pw && (
                  <Box sx={{ display:"flex", flexWrap:"wrap", gap:1, mb:2.5 }}>
                    {REQUIREMENTS.map((r) => (
                      <Box key={r.label} sx={{ display:"flex", alignItems:"center", gap:0.6, px:1.25, py:0.5, borderRadius:"20px",
                        bgcolor: r.test(pw)
                          ? isDark ? "rgba(16,185,129,0.12)" : "#f0fdf4"
                          : isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                        border:`1px solid ${r.test(pw) ? isDark ? "rgba(16,185,129,0.25)" : "#bbf7d0" : isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
                      }}>
                        <Box sx={{ width:6, height:6, borderRadius:"50%", bgcolor: r.test(pw) ? "#10b981" : "#d1d5db", flexShrink:0 }}/>
                        <Typography sx={{ fontSize:11, color: r.test(pw) ? isDark ? "#6ee7b7" : "#15803d" : isDark ? "#64748b" : "#9ca3af" }}>
                          {r.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Confirm password */}
                <Typography sx={{ fontSize:12, fontWeight:700, color: isDark ? "#cbd5e1" : "#374151", mb:0.75, letterSpacing:"0.4px", textTransform:"uppercase" }}>
                  Confirm Password
                </Typography>
                <TextField fullWidth size="small" placeholder="Confirm new password"
                  type={showCf ? "text" : "password"}
                  value={confirm} onChange={(e) => { setConfirm(e.target.value); setError(""); }}
                  error={!!error} helperText={error}
                  sx={{
                    mb:3,
                    "& .MuiOutlinedInput-root":{ borderRadius:"12px", fontSize:14,
                      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                      "& fieldset":{
                        borderColor: confirm && pw === confirm ? "#10b981" : error ? "#ef4444" : isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
                      },
                      "&:hover fieldset":{ borderColor:"#6366f1" },
                      "&.Mui-focused fieldset":{ borderColor:"#6366f1", borderWidth:2 },
                    },
                    "& input":{ color: isDark ? "#f1f5f9" : "#0f172a" },
                  }}
                  InputProps={{
                    startAdornment:<InputAdornment position="start"><FaLock size={13} color={isDark ? "#475569" : "#94a3b8"}/></InputAdornment>,
                    endAdornment:(
                      <InputAdornment position="end">
                        {confirm && pw === confirm
                          ? <FaCheckCircle size={14} color="#10b981"/>
                          : <IconButton size="small" onClick={() => setShowCf((p) => !p)}>
                              {showCf ? <FaEyeSlash size={13} color="#94a3b8"/> : <FaEye size={13} color="#94a3b8"/>}
                            </IconButton>
                        }
                      </InputAdornment>
                    ),
                  }}
                />

                <Button fullWidth variant="contained" disableElevation type="submit" disabled={loading}
                  sx={{
                    height:50, textTransform:"none", fontWeight:700, fontSize:15, borderRadius:"12px",
                    background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
                    boxShadow:"0 4px 20px rgba(99,102,241,0.4)",
                    "&:hover":{ background:"linear-gradient(135deg,#4f46e5,#7c3aed)", boxShadow:"0 6px 28px rgba(99,102,241,0.5)", transform:"translateY(-1px)" },
                    "&:disabled":{ background:"#c7d2fe", boxShadow:"none" },
                    transition:"all 0.2s",
                  }}>
                  {loading ? <CircularProgress size={20} sx={{ color:"#fff" }}/> : "Reset Password"}
                </Button>
              </form>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPasswordForm;
